import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Task } from '../../types/task';
import { Button } from '../atoms/Button/Button';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { useTranslation } from '../../i18n/useTranslation';
import { useTaskOccurrences } from '../../hooks/useTasks';
import {
  addDays,
  addMonths,
  startOfMonth,
  startOfWeek,
  todayIso,
  useTaskFormat,
  weekdayOf,
  type TaskFormat,
} from './taskFormat';
import './TaskCalendar.css';

type CalendarView = 'month' | 'week' | 'day';

interface TaskCalendarProps {
  aquariumId: string;
  onOpenTask: (task: Task) => void;
  /**
   * Tapping a day without tasks starts the create flow for that date (YYYY-MM-DD).
   * Without it, every day opens the day view.
   */
  onCreateTask?: (date: string) => void;
  /** Initial date shown (YYYY-MM-DD); defaults to today. */
  initialDate?: string;
}

const MAX_CHIPS_PER_CELL = 3;

function chipClass(task: Task): string {
  const classes = ['task-calendar__chip', `task-calendar__chip--${task.priority}`];
  if (task.status === 'completed' || task.status === 'skipped') classes.push('task-calendar__chip--done');
  return classes.join(' ');
}

function groupByDate(tasks: Task[]): Map<string, Task[]> {
  const map = new Map<string, Task[]>();
  for (const task of tasks) {
    const date = task.dueDate?.slice(0, 10);
    if (!date) continue;
    const list = map.get(date) ?? [];
    list.push(task);
    map.set(date, list);
  }
  return map;
}

function TaskChip({ task, fmt, block = false, onOpenTask }: {
  task: Task;
  fmt: TaskFormat;
  block?: boolean;
  onOpenTask: (task: Task) => void;
}) {
  const { t } = useTranslation();
  const statusText = task.status === 'completed'
    ? t('tasks.calendar.statusDone')
    : task.status === 'skipped' ? t('tasks.calendar.statusSkipped') : null;
  return (
    <Button
      type="button"
      variant="ghost"
      className={`${chipClass(task)}${block ? ' task-calendar__chip--block' : ''}`}
      onClick={() => onOpenTask(task)}
      title={task.title}
      data-testid={`calendar-chip-${task.id}`}
    >
      {task.dueTime && <span className="task-calendar__chip-time">{fmt.time(task.dueTime)} </span>}
      {task.title}
      {statusText && <span className="task-calendar__sr-only"> ({statusText})</span>}
    </Button>
  );
}

/** Accessible name of a day target: what tapping it does. */
function useDayActionLabel(fmt: TaskFormat, canCreate: boolean) {
  const { t } = useTranslation();
  return (date: string, taskCount: number) => (
    taskCount === 0 && canCreate
      ? t('tasks.calendar.createOnDay', { date: fmt.longDate(date) })
      : t('tasks.calendar.showDay', { date: fmt.longDate(date) })
  );
}

function MonthView({ days, month, byDate, todayStr, fmt, onOpenTask, onShowDay, onDayClick, dayLabel }: {
  days: string[];
  month: string;
  byDate: Map<string, Task[]>;
  todayStr: string;
  fmt: TaskFormat;
  onOpenTask: (task: Task) => void;
  onShowDay: (date: string) => void;
  onDayClick: (date: string) => void;
  dayLabel: (date: string, taskCount: number) => string;
}) {
  const { t } = useTranslation();
  return (
    <div className="task-calendar__month">
      <div className="task-calendar__weekdays" aria-hidden="true">
        {fmt.weekdays('short').map(({ day, label }) => (
          <div key={day} className="task-calendar__weekday" data-testid="calendar-weekday">{label}</div>
        ))}
      </div>
      <ol className="task-calendar__grid">
        {days.map((date) => {
          const dayTasks = byDate.get(date) ?? [];
          const isThisMonth = date.slice(0, 7) === month;
          const isToday = date === todayStr;
          return (
            <li
              key={date}
              className={[
                'task-calendar__cell',
                !isThisMonth && 'task-calendar__cell--other-month',
                isToday && 'task-calendar__cell--today',
              ].filter(Boolean).join(' ')}
              data-date={date}
              aria-label={fmt.longDate(date)}
              aria-current={isToday ? 'date' : undefined}
            >
              {/* The day number is the day's button; its ::after stretches over the whole cell,
                  so a tap anywhere outside a task chip opens the day (one tab stop per day). */}
              <Button
                type="button"
                variant="ghost"
                className="task-calendar__day-btn"
                onClick={() => onDayClick(date)}
                aria-label={dayLabel(date, dayTasks.length)}
                data-testid={`calendar-day-${date}`}
              >
                <span className="task-calendar__cell-num" aria-hidden="true">{Number(date.slice(8, 10))}</span>
              </Button>
              <div className="task-calendar__cell-tasks">
                {dayTasks.slice(0, MAX_CHIPS_PER_CELL).map((task) => (
                  <TaskChip key={task.id} task={task} fmt={fmt} onOpenTask={onOpenTask} />
                ))}
                {dayTasks.length > MAX_CHIPS_PER_CELL && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="task-calendar__chip-overflow"
                    onClick={() => onShowDay(date)}
                  >
                    {t('tasks.calendar.more', { count: dayTasks.length - MAX_CHIPS_PER_CELL })}
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function WeekView({ days, byDate, todayStr, fmt, onOpenTask, onDayClick, dayLabel }: {
  days: string[];
  byDate: Map<string, Task[]>;
  todayStr: string;
  fmt: TaskFormat;
  onOpenTask: (task: Task) => void;
  onDayClick: (date: string) => void;
  dayLabel: (date: string, taskCount: number) => string;
}) {
  return (
    <ol className="task-calendar__week">
      {days.map((date) => {
        const dayTasks = byDate.get(date) ?? [];
        const isToday = date === todayStr;
        return (
          <li
            key={date}
            className={['task-calendar__week-col', isToday && 'task-calendar__week-col--today'].filter(Boolean).join(' ')}
            data-date={date}
            aria-label={fmt.longDate(date)}
            aria-current={isToday ? 'date' : undefined}
          >
            <Button
              type="button"
              variant="ghost"
              className="task-calendar__week-col-header"
              onClick={() => onDayClick(date)}
              aria-label={dayLabel(date, dayTasks.length)}
              data-testid={`calendar-day-${date}`}
            >
              <span className="task-calendar__week-day-name" aria-hidden="true">{fmt.weekdayName(weekdayOf(date), 'short')}</span>
              <span
                className={`task-calendar__week-day-num${isToday ? ' task-calendar__week-day-num--today' : ''}`}
                aria-hidden="true"
              >
                {Number(date.slice(8, 10))}
              </span>
            </Button>
            <div className="task-calendar__week-tasks">
              {dayTasks.map((task) => (
                <TaskChip key={task.id} task={task} fmt={fmt} block onOpenTask={onOpenTask} />
              ))}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function DayView({ dayTasks, fmt, onOpenTask, focusFirst, onFocused }: {
  dayTasks: Task[];
  fmt: TaskFormat;
  onOpenTask: (task: Task) => void;
  /** Move focus to the first task (the day's button that was activated is gone). */
  focusFirst: boolean;
  onFocused: () => void;
}) {
  const { t } = useTranslation();
  const firstRowRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (focusFirst && firstRowRef.current) {
      firstRowRef.current.focus();
      onFocused();
    }
  }, [focusFirst, onFocused, dayTasks.length]);
  if (dayTasks.length === 0) {
    return (
      <div className="task-calendar__day">
        <Text color="muted" className="task-calendar__day-empty">{t('tasks.calendar.dayEmpty')}</Text>
      </div>
    );
  }
  return (
    <div className="task-calendar__day">
      <ul className="task-calendar__day-list">
        {dayTasks.map((task, index) => (
          <li key={task.id}>
            <Button
              ref={index === 0 ? firstRowRef : undefined}
              type="button"
              variant="ghost"
              className={`task-calendar__day-row${task.status === 'completed' || task.status === 'skipped' ? ' task-calendar__day-row--done' : ''}`}
              onClick={() => onOpenTask(task)}
              data-testid={`calendar-chip-${task.id}`}
            >
              <span className="task-calendar__day-row-time">{task.dueTime ? fmt.time(task.dueTime) : ''}</span>
              <span className="task-calendar__day-row-title">{task.title}</span>
              <span className="task-calendar__day-row-meta">
                {fmt.priority(task.priority)} · {fmt.category(task.category)}
                {task.status !== 'open' && task.status !== 'in_progress' ? ` · ${fmt.status(task.status)}` : ''}
              </span>
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TaskCalendar({ aquariumId, onOpenTask, onCreateTask, initialDate }: TaskCalendarProps) {
  const { t } = useTranslation();
  const fmt = useTaskFormat();
  const todayStr = useMemo(() => todayIso(), []);
  const [view, setView] = useState<CalendarView>('month');
  const [navDate, setNavDate] = useState<string>(initialDate ?? todayStr);
  const [focusDay, setFocusDay] = useState(false);
  const clearFocusDay = useCallback(() => setFocusDay(false), []);
  const dayLabel = useDayActionLabel(fmt, Boolean(onCreateTask));

  const range = useMemo(() => {
    if (view === 'month') {
      const from = startOfWeek(startOfMonth(navDate), fmt.firstDayOfWeek);
      return { from, to: addDays(from, 41) };
    }
    if (view === 'week') {
      const from = startOfWeek(navDate, fmt.firstDayOfWeek);
      return { from, to: addDays(from, 6) };
    }
    return { from: navDate, to: navDate };
  }, [view, navDate, fmt.firstDayOfWeek]);

  const days = useMemo(() => {
    const count = view === 'month' ? 42 : view === 'week' ? 7 : 1;
    return Array.from({ length: count }, (_, i) => addDays(range.from, i));
  }, [view, range.from]);

  const { occurrences, isLoading, isError, refetch } = useTaskOccurrences(aquariumId, range.from, range.to);
  const byDate = useMemo(() => groupByDate(occurrences), [occurrences]);

  function showDay(date: string) {
    setNavDate(date);
    setView('day');
    setFocusDay(true);
  }

  /** Tap on a day: its tasks in the day view, or — for an empty day — a new task on that date. */
  function handleDayClick(date: string) {
    const hasTasks = (byDate.get(date)?.length ?? 0) > 0;
    // While loading we cannot know the day is empty: show the day instead of creating.
    if (!hasTasks && !isLoading && onCreateTask) onCreateTask(date);
    else showDay(date);
  }

  function shift(direction: 1 | -1) {
    setFocusDay(false);
    setNavDate((prev) => {
      if (view === 'month') return addMonths(startOfMonth(prev), direction);
      if (view === 'week') return addDays(prev, 7 * direction);
      return addDays(prev, direction);
    });
  }

  const periodLabel = useMemo(() => {
    if (view === 'month') return fmt.monthYear(navDate);
    if (view === 'week') return `${fmt.date(range.from)} – ${fmt.date(range.to)}`;
    return fmt.longDate(navDate);
  }, [view, navDate, range, fmt]);

  return (
    <section className="task-calendar" aria-label={t('tasks.calendar.label')}>
      <div className="task-calendar__toolbar">
        <div className="task-calendar__nav-group">
          <Button variant="ghost" className="task-calendar__nav-btn" onClick={() => shift(-1)} aria-label={t('tasks.calendar.previous')}>
            <span aria-hidden="true">‹</span>
          </Button>
          <Heading level={2} className="task-calendar__period-label">{periodLabel}</Heading>
          <Button variant="ghost" className="task-calendar__nav-btn" onClick={() => shift(1)} aria-label={t('tasks.calendar.next')}>
            <span aria-hidden="true">›</span>
          </Button>
        </div>

        <Button variant="secondary" size="sm" onClick={() => {
          setFocusDay(false);
          setNavDate(todayStr);
        }}>
          {t('tasks.calendar.today')}
        </Button>

        <div className="task-calendar__view-toggle" role="group" aria-label={t('tasks.calendar.viewLabel')}>
          {(['month', 'week', 'day'] as CalendarView[]).map((v) => (
            <Button
              key={v}
              variant="ghost"
              className={`task-calendar__view-btn${view === v ? ' task-calendar__view-btn--active' : ''}`}
              onClick={() => {
                setFocusDay(false);
                setView(v);
              }}
              aria-pressed={view === v}
              size="sm"
            >
              {t(`tasks.calendar.views.${v}`)}
            </Button>
          ))}
        </div>
      </div>

      {isError ? (
        <LoadErrorState title={t('tasks.calendar.loadError')} onRetry={() => void refetch()} />
      ) : (
        <div className="task-calendar__body" aria-busy={isLoading || undefined}>
          {isLoading && <LiveRegion visuallyHidden busy>{t('tasks.calendar.loading')}</LiveRegion>}
          {view === 'month' && (
            <MonthView
              days={days}
              month={navDate.slice(0, 7)}
              byDate={byDate}
              todayStr={todayStr}
              fmt={fmt}
              onOpenTask={onOpenTask}
              onShowDay={showDay}
              onDayClick={handleDayClick}
              dayLabel={dayLabel}
            />
          )}
          {view === 'week' && (
            <WeekView
              days={days}
              byDate={byDate}
              todayStr={todayStr}
              fmt={fmt}
              onOpenTask={onOpenTask}
              onDayClick={handleDayClick}
              dayLabel={dayLabel}
            />
          )}
          {view === 'day' && (
            <DayView
              dayTasks={byDate.get(navDate) ?? []}
              fmt={fmt}
              onOpenTask={onOpenTask}
              focusFirst={focusDay}
              onFocused={clearFocusDay}
            />
          )}
        </div>
      )}
    </section>
  );
}
